// components/esim/EsimFilters.jsx
"use client";

import React, { useState } from "react";

// Plan-type tabs: separate single-country plans from multi-country bundles.
const PLAN_TYPES = [
  { key: "all",      label: "All" },
  { key: "country",  label: "🏳️ Single Country" },
  { key: "regional", label: "🌍 Regional" },
];

export default function EsimFilters({
  filters,
  options,
  onChange,
  disabled = false,
  planType = "all",
  onPlanTypeChange,
  regionalRegions,
}) {
  const [collapsed, setCollapsed] = useState(false);

  const { country = "", regions: regionVal = "", subType = "" } = filters || {};
  const { countries = [], regions = [], subTypes = [] } = options || {};

  const isRegional = planType === "regional";
  // In regional mode, offer the multi-country region list; fall back to facets.
  const regionOptions =
    isRegional && Array.isArray(regionalRegions) && regionalRegions.length
      ? regionalRegions
      : regions;

  const resetAll = () => {
    onChange("regions", "");
    onChange("country", "");
    onChange("subType", "");
  };

  const chips = [
    regionVal && { key: "regions", label: regionVal },
    country && {
      key: "country",
      label: countries.find((c) => c.code === country)?.name || country,
    },
    subType && { key: "subType", label: subType },
  ].filter(Boolean);

  return (
    <div className="bg-surface border border-gray-700 rounded-xl p-3 sm:p-4 text-textPrimary">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-textSecondary">Filters</span>
          {chips.length === 0 ? (
            <span className="text-xs text-textSecondary">None</span>
          ) : (
            chips.map((c) => (
              <button
                type="button"
                key={`${c.key}:${c.label}`}
                onClick={() => onChange(c.key, "")}
                disabled={disabled}
                className="text-xs px-2 py-1 rounded-full bg-gray-700 hover:bg-gray-600"
              >
                {c.label} <span className="ml-1">×</span>
              </button>
            ))
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            className="text-xs px-2 py-1 rounded-md border border-gray-700 hover:bg-gray-800"
            onClick={resetAll}
            disabled={disabled || chips.length === 0}
          >
            Reset
          </button>
          <button
            type="button"
            className="text-xs px-2 py-1 rounded-md border border-gray-700 hover:bg-gray-800"
            onClick={() => setCollapsed((v) => !v)}
          >
            {collapsed ? "Show" : "Hide"}
          </button>
        </div>
      </div>

      {/* Plan type: single-country vs multi-country regional bundles */}
      {onPlanTypeChange && (
        <div className="mt-3 grid grid-cols-3 gap-1 rounded-lg bg-background p-1 border border-gray-700">
          {PLAN_TYPES.map((t) => (
            <button
              type="button"
              key={t.key}
              onClick={() => onPlanTypeChange(t.key)}
              disabled={disabled}
              aria-pressed={planType === t.key}
              className={`text-xs px-2 py-1.5 rounded-md transition-colors ${
                planType === t.key
                  ? "bg-primary text-white"
                  : "text-textSecondary hover:bg-gray-800"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {!collapsed && (
        <div className="mt-3 grid grid-cols-1 gap-3">
          {/* Region — primary selector for regional bundles */}
          <div>
            <label
              htmlFor="esim-region"
              className="block text-sm font-medium text-textSecondary mb-1"
            >
              🌍 Region
            </label>
            <select
              id="esim-region"
              className="w-full p-2 border border-gray-700 bg-background text-textPrimary rounded-md text-sm shadow-sm"
              value={regionVal}
              onChange={(e) => onChange("regions", e.target.value)}
              disabled={disabled}
            >
              <option value="">All Regions</option>
              {regionOptions.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          {/* Country — hidden in regional mode (bundles have no single country) */}
          {!isRegional && (
            <div>
              <label
                htmlFor="esim-country"
                className="block text-sm font-medium text-textSecondary mb-1"
              >
                🗺️ Country
              </label>
              <select
                id="esim-country"
                className="w-full p-2 border border-gray-700 bg-background text-textPrimary rounded-md text-sm shadow-sm"
                value={country}
                onChange={(e) => onChange("country", e.target.value)}
                disabled={disabled}
              >
                <option value="">All Countries</option>
                {countries.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name || c.code}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Subtype */}
          <div>
            <label
              htmlFor="esim-subtype"
              className="block text-sm font-medium text-textSecondary mb-1"
            >
              ⚡ Subtype
            </label>
            <select
              id="esim-subtype"
              className="w-full p-2 border border-gray-700 bg-background text-textPrimary rounded-md text-sm shadow-sm"
              value={subType}
              onChange={(e) => onChange("subType", e.target.value)}
              disabled={disabled}
            >
              <option value="">All Types</option>
              {subTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}
